import { decodeDisplayText } from './displayText';
import React from 'react';

import {
    findSearchHighlightRanges,
    getSearchHighlightTerms
} from './searchHighlightCore';

export {
    buildSearchMatchPreviewExcerpt,
    buildSearchMatchSnippet,
    countSearchMatches,
    escapeSearchRegex,
    findSearchHighlightRanges,
    getSearchHighlightTerms,
    hasSearchHighlightMatch,
    isStemmableSearchTerm,
    simpleStem,
    stripMarkdownForPlainText
} from './searchHighlightCore';

const DEFAULT_HIGHLIGHT_STYLE = {
    backgroundColor: 'rgba(255, 214, 102, 0.42)',
    color: 'inherit',
    borderRadius: 3,
    padding: '0 2px',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone'
};

export const renderHighlightedSearchText = (text, searchTerm, options = {}) => {
    // Pass non-string/non-number values (null, undefined, arrays, objects,
    // React elements) through unchanged rather than stringifying them into
    // something like "[object Object]".
    if (text != null && typeof text !== 'string' && typeof text !== 'number') {
        return text;
    }

    const source = decodeDisplayText(String(text || ''));
    if (!source) {
        return source;
    }

    const ranges = findSearchHighlightRanges(source, searchTerm);
    if (!ranges.length) {
        return source;
    }

    const className = options.className || 'search-hit-highlight';
    const style = options.style || DEFAULT_HIGHLIGHT_STYLE;
    const nodes = [];
    let cursor = 0;

    ranges.forEach((range, index) => {
        if (cursor < range.start) {
            nodes.push(source.slice(cursor, range.start));
        }

        nodes.push(
            <span key={`search-highlight-${range.start}-${range.end}-${index}`} className={className} style={style}>
                {source.slice(range.start, range.end)}
            </span>
        );
        cursor = range.end;
    });

    if (cursor < source.length) {
        nodes.push(source.slice(cursor));
    }

    return nodes;
};

// Tags whose text content should not be highlighted (highlighting inside code
// blocks or links tends to break copy/paste and read as visually confusing).
const HIGHLIGHT_SKIP_TAGS = new Set(['code', 'pre', 'a']);

/**
 * Recursively walks a rendered React tree (e.g. the output of a markdown
 * renderer) and wraps matching text nodes with the search highlight span,
 * leaving element structure/formatting untouched.
 */
export const highlightReactChildren = (node, searchTerm, options = {}) => {
    // Non-null objects/arrays/elements that aren't real React nodes, or a
    // non-string search term, should pass through untouched rather than risk
    // throwing deep inside the tree walk below.
    const terms = getSearchHighlightTerms(searchTerm);
    if (!terms.length) {
        return node;
    }

    const walk = (child, keyPrefix) => {
        if (child == null || typeof child === 'boolean') {
            return child;
        }

        if (typeof child === 'number') {
            return child;
        }

        if (typeof child === 'string') {
            const highlighted = renderHighlightedSearchText(child, searchTerm, options);
            return highlighted === child ? child : <React.Fragment key={keyPrefix}>{highlighted}</React.Fragment>;
        }

        if (Array.isArray(child)) {
            return child.map((c, index) => walk(c, `${keyPrefix}-${index}`));
        }

        if (!React.isValidElement(child)) {
            return child;
        }

        // React.Fragment (including the <>...</> shorthand) has no DOM
        // representation of its own - it's just a grouping wrapper around
        // its children, so it's always safe to recurse into.
        if (child.type === React.Fragment) {
            const fragmentProps = child.props || {};
            return React.cloneElement(
                child,
                { key: child.key ?? keyPrefix },
                walk(fragmentProps.children, `${keyPrefix}-f`)
            );
        }

        // Only ever rewrite the children of plain DOM/host elements (e.g. a
        // rendered <p>/<span> tag), plus local wrapper components that opt in
        // after markdown has already been compiled. Custom component elements
        // (like the markdown-to-jsx <Markdown> element) can hold raw markdown
        // source strings in `props.children`; splicing highlight spans into
        // those strings crashes the library's internal string-only parsing
        // (TypeError: e.replace is not a function).
        if (typeof child.type !== 'string') {
            if (!child.type?.searchHighlightTraverseChildren) {
                return child;
            }

            const childProps = child.props || {};
            if (childProps.children == null) {
                return child;
            }

            return React.cloneElement(
                child,
                { key: child.key ?? keyPrefix },
                walk(childProps.children, `${keyPrefix}-c`)
            );
        }

        const tagName = child.type.toLowerCase();
        if (HIGHLIGHT_SKIP_TAGS.has(tagName)) {
            return child;
        }

        const childProps = child.props || {};
        if (childProps.children == null) {
            return child;
        }

        return React.cloneElement(
            child,
            { key: child.key ?? keyPrefix },
            walk(childProps.children, `${keyPrefix}-c`)
        );
    };

    return walk(node, 'search-highlight-tree');
};
